import * as CookieConsent from "vanilla-cookieconsent";

// Initialize cookie consent
const initCookieConsent = () => {
	window.cookieConsent = CookieConsent;

	// Run the cookie consent banner
	CookieConsent.run({
		disablePageInteraction: true,
		guiOptions: {
			consentModal: {
				layout: "bar",
				position: "bottom",
				equalWeightButtons: true,
				flipButtons: true,
			},
			preferencesModal: {
				layout: "box",
				position: "right",
				equalWeightButtons: true,
				flipButtons: false,
			},
		},

		categories: {
			necessary: {
				enabled: true,
				readOnly: true,
			},
			external: {
				enabled: false,
				readOnly: false,
				services: {
					youtube: {
						label: "YouTube",
					},
					googleMaps: {
						label: "Google Maps",
					},
					facebook: {
						label: "Facebook",
					},
				},
			},
		},

		language: {
			default: "de",
			autoDetect: "document",
			translations: {
				de: {
					consentModal: {
						title: "Wir verwenden Cookies",
						description:
							"Wir verwenden Cookies, um die grundlegenden Funktionen der Website zu gewährleisten und Ihr Surferlebnis zu verbessern. Sie können für jede Kategorie wählen, ob Sie zustimmen oder ablehnen möchten.",
						acceptAllBtn: "Alle akzeptieren",
						acceptNecessaryBtn:
							"Nur essenzielle Cookies akzeptieren",
						showPreferencesBtn: "Einstellungen verwalten",
					},
					preferencesModal: {
						title: "Cookie-Einstellungen verwalten",
						acceptAllBtn: "Alle akzeptieren",
						acceptNecessaryBtn:
							"Nur essenzielle Cookies akzeptieren",
						savePreferencesBtn: "Auswahl speichern",
						closeIconLabel: "Schließen",
						sections: [
							{
								title: "Cookie-Verwendung",
								description:
									"Wir verwenden Cookies, um die grundlegenden Funktionen der Website zu gewährleisten und Ihr Surferlebnis zu verbessern. Sie können für jede Kategorie wählen, ob Sie zustimmen oder ablehnen möchten.",
							},
							{
								title: "Unbedingt erforderliche Cookies",
								description:
									"Diese Cookies sind für das ordnungsgemäße Funktionieren der Website unerlässlich. Sie ermöglichen die Nutzung sicherer Bereiche, Formulare und weitere grundlegende Funktionen. Ohne diese Cookies kann die Website nicht richtig funktionieren. Diese beinhalten den CRAFT_CSRF_TOKEN für Sicherheitsabfragen und CraftSessionId für Ihre Sitzung.",
								linkedCategory: "necessary",
							},
							{
								title: "Externe Dienste",
								description:
									"Diese Dienste werden von Drittanbietern bereitgestellt und können Daten sammeln. Sie können einzelne Dienste aktivieren oder deaktivieren:",
								linkedCategory: "external",
								cookieTable: {
									caption: "Verfügbare externe Dienste",
									headers: {
										name: "Dienst",
										domain: "Anbieter",
										desc: "Beschreibung",
									},
									body: [
										{
											name: "YouTube",
											domain: "YouTube (Google)",
											desc: "Ermöglicht das Einbetten und Anzeigen von YouTube-Videos",
										},
										{
											name: "Google Maps",
											domain: "Google",
											desc: "Ermöglicht das Einbetten von interaktiven Karten",
										},
										{
											name: "Facebook",
											domain: "Meta (Facebook)",
											desc: "Ermöglicht das Einbetten von Facebook-Inhalten wie Posts und Videos",
										},
									],
								},
							},
							{
								title: "Weitere Informationen",
								description:
									'Bei Fragen zu unserer Cookie-Richtlinie und Ihren Wahlmöglichkeiten kontaktieren Sie uns bitte über <a href="#contact-page">unsere Kontaktseite</a>.',
							},
						],
					},
				},
				en: {
					consentModal: {
						title: "We use cookies",
						description:
							"We use cookies to ensure the basic functionality of the website and to enhance your browsing experience. You can choose to opt in or out of each category.",
						acceptAllBtn: "Accept all",
						acceptNecessaryBtn: "Accept only essential cookies",
						showPreferencesBtn: "Manage preferences",
					},
					preferencesModal: {
						title: "Manage cookie preferences",
						acceptAllBtn: "Accept all",
						acceptNecessaryBtn: "Accept only essential cookies",
						savePreferencesBtn: "Save preferences",
						closeIconLabel: "Close",
						sections: [
							{
								title: "Cookie Usage",
								description:
									"We use cookies to ensure the basic functionality of the website and to enhance your browsing experience. You can choose to opt in or out of each category.",
							},
							{
								title: "Strictly Necessary Cookies",
								description:
									"These cookies are essential for the proper functioning of the website. They enable secure areas, forms, and other basic features. Without these cookies, the website cannot function properly. These include the CRAFT_CSRF_TOKEN for security queries and CraftSessionId for your session.",
								linkedCategory: "necessary",
							},
							{
								title: "External Services",
								description:
									"These services are provided by third parties and may collect data. You can enable or disable individual services:",
								linkedCategory: "external",
								cookieTable: {
									caption: "Available external services",
									headers: {
										name: "Service",
										domain: "Provider",
										desc: "Description",
									},
									body: [
										{
											name: "YouTube",
											domain: "YouTube (Google)",
											desc: "Allows embedding and displaying YouTube videos",
										},
										{
											name: "Google Maps",
											domain: "Google",
											desc: "Allows embedding interactive maps",
										},
										{
											name: "Facebook",
											domain: "Meta (Facebook)",
											desc: "Allows embedding Facebook content like posts and videos",
										},
									],
								},
							},
							{
								title: "More Information",
								description:
									'For any questions regarding our cookie policy and your choices, please contact us via <a href="#contact-page">our contact page</a>.',
							},
						],
					},
				},
			},
		},

		onAccept: () => {
			// Handle external services acceptance
			if (window.cookieConsent.acceptedCategory("external")) {
				// Trigger iframe manager to unblock accepted services
				const acceptedServices =
					window.cookieConsent.getUserPreferences().acceptedServices
						?.external || [];

				// Notify iframe manager of accepted services
				if (window.iframemanager && acceptedServices.length > 0) {
					// Force iframe manager to recheck and unblock accepted services
					acceptedServices.forEach((service) => {
						window.iframemanager.acceptService(service);
					});
				}
			}
		},

		onChange: ({ changedCategories, changedServices }) => {
			// Handle changes in external services
			if (
				changedCategories.includes("external") ||
				changedServices?.external
			) {
				const acceptedServices =
					window.cookieConsent.getUserPreferences().acceptedServices
						?.external || [];

				if (window.iframemanager) {
					// Update iframe manager with current service states
					acceptedServices.forEach((service) => {
						window.iframemanager.acceptService(service);
					});
				}
			}
		},
	});
};

export default {
	initCookieConsent,
};
